import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Card,
  CardContent,
  DataTable,
  EmptyState,
  LoadingSpinner,
  toast,
  type DataTableColumn,
} from '@atrvd/saas-ui'
import { AlertTriangle, Building2, Landmark, Loader2, RefreshCw } from 'lucide-react'
import { formatDateTime } from '@dcp/shared/utils/formatters'
import {
  useBankConnections,
  useDeleteBankConnection,
  useFacilityConnectionAccountCounts,
} from '@/hooks/useBankConnections'
import {
  useReauthorizeConnection,
  useTriggerLedgerSyncSyncAllOrg,
  useTriggerLedgerSyncSyncBalancesAllOrg,
  useTriggerLedgerSyncSyncConnection,
} from '@/hooks/useLedgerSync'
import { useTableColumnPrefs } from '@/hooks/useTableColumnPrefs'
import { useLedgerSyncCompletionWatcher } from '@/hooks/useLedgerSyncCompletionWatcher'
import type { LedgerSyncSyncPipeline } from '@dcp/shared/ledgersync/sync-state'
import { getConnectionSyncActivity, isPipelineSyncActive } from '@/lib/ledgersync-sync-activity'
import { getLedgerSyncOAuthState } from '@/lib/ledgersync-oauth'
import { ConfirmationDialog } from '@/components/admin/ConfirmationDialog'
import {
  financialAccountsBasePath,
  type FacilityFinancialAccountsScope,
} from '@/components/financial-accounts/financialAccountsScope'
import { ReadOnlyActionMenu } from '@/components/financial-accounts/ReadOnlyControls'
import { LedgerSyncConnectButton } from './LedgerSyncConnectButton'
import { LedgerSyncConnectionReconciler } from './LedgerSyncConnectionReconciler'
import { CapabilityBadges } from './CapabilityBadges'
import { RenameConnectionDialog } from './RenameConnectionDialog'
import { SyncFailureIndicator } from './SyncFailureIndicator'
import { ConnectionStatusBadge } from './ConnectionStatusBadge'
import {
  bankDataAsOfLabel,
  isFirstConnectInProgress,
  presentConnectionStatus,
} from './ledgerSyncConnectedBanks.utils'
import { CONNECTION_COPY, bankInitials, facilityConnectionsEmptyDescription } from './connectionManagement.utils'
import { scopeConnectionsToFacility } from './facilityScope.utils'
import type { BankConnectionRow } from '@/lib/db-tenant'
import { BANK_CONNECTION_SORT_COLUMNS_LIST, type BankConnectionSortColumn } from '@/lib/db-tenant'

/**
 * Local sort-state shape matching DataTable's `sorting.sortState` prop
 * (data-table/types.ts `SortState` — `{ columnKey, direction }`). Declared
 * inline for the same reason `OutstandingChecksPage` does: the saas-ui barrel
 * exports two `SortState` interfaces and the wrong one (the hook's
 * `{ key, direction }`) wins in the public type.
 */
interface DataTableSortState {
  columnKey: string
  direction: 'asc' | 'desc' | null
}

/** Runtime guard so a DataTable column key can only reach the RPC's whitelist. */
const SORTABLE = new Set<string>(BANK_CONNECTION_SORT_COLUMNS_LIST)

/** Which destructive/confirmable action the dialog is currently asking about. */
type PendingAction = { kind: 'delete'; connection: BankConnectionRow }

/** Props for {@link BankConnectionsSection}. */
interface BankConnectionsSectionProps {
  /**
   * The facility whose page this is; null on the organization-wide page.
   * Scoped, the list shows only connections with accounts at the facility,
   * each counting only those, and drops the org-level "Sync all" buttons.
   */
  scope: FacilityFinancialAccountsScope | null
}

/**
 * Bank Connections — the org-level connection list (DCP-324, Screen 1).
 *
 * Rendered ABOVE the existing per-facility overview on `/settings/financial-accounts`,
 * because a connection is an ORGANIZATION-level object since this ticket: it is
 * one bank login whose accounts may be spread over any number of facilities.
 *
 * SORTING IS SERVER-SIDE (AC#4). Every column here sorts on something derived —
 * `display_name` is a COALESCE, `accounts_count` and `last_sync_at` are
 * reductions over `bank_accounts`, and Capabilities sorts on labels pulled out
 * of JSON — so the ordering is the RPC's job and `sorting.mode` is `'server'`.
 * Sorting client-side would need the whole account set per connection in the
 * browser just to order a count.
 *
 * FACILITY SCOPE. On a facility's page (`scope`) the same list is narrowed on
 * the client to the connections with accounts at that facility, using the
 * facility's per-connection counts; the server sort still applies, except
 * "Accounts & cards", which is re-sorted by the facility's count.
 *
 * NO PAGINATION and no lazy loading. An org has tens of connections, and the
 * list's purpose is to be read at a glance; a page control would hide banks
 * behind a click for no benefit. (The per-connection ACCOUNTS table is the
 * opposite case and is lazily loaded — see `ConnectionAccountsTable`.)
 */
export function BankConnectionsSection({ scope }: BankConnectionsSectionProps) {
  const navigate = useNavigate()
  /** This page — where drill-downs hang and where Connect / Reconnect return to. */
  const basePath = financialAccountsBasePath(scope)
  /** An inactive facility's page: the row kebab is disabled (ADR 0016 exception). */
  const readOnly = scope?.readOnly ?? false

  /**
   * Server-mode sort state. Single-column: DataTable supports Ctrl+Click
   * multi-sort, but `get_bank_connections` takes one column + one direction.
   */
  const [sortState, setSortState] = useState<DataTableSortState[]>([])

  // Server sort: pluck the active column, ignoring a toggled-off column (the
  // library reports `direction: null`) so the query falls back to the RPC's
  // default ordering rather than sending a meaningless direction.
  const activeSort = sortState[0]
  const sortColumn =
    activeSort && activeSort.direction !== null && SORTABLE.has(activeSort.columnKey)
      ? (activeSort.columnKey as BankConnectionSortColumn)
      : undefined
  const sortDirection =
    activeSort && activeSort.direction !== null && sortColumn ? activeSort.direction : undefined

  const {
    data: connections,
    dataUpdatedAt,
    isLoading,
    error,
  } = useBankConnections({ column: sortColumn, direction: sortDirection })
  const facilityCounts = useFacilityConnectionAccountCounts(scope?.facilityId ?? null)

  const deleteConnection = useDeleteBankConnection()
  const syncConnection = useTriggerLedgerSyncSyncConnection()
  const syncAll = useTriggerLedgerSyncSyncAllOrg()
  const syncBalancesAll = useTriggerLedgerSyncSyncBalancesAllOrg()
  const reauthorize = useReauthorizeConnection()

  /** Institution logos that failed to load, so a broken image is not retried. */
  const [brokenLogos, setBrokenLogos] = useState<Record<string, boolean>>({})
  /** The row whose rename dialog is open; null when none. */
  const [renameId, setRenameId] = useState<string | null>(null)
  /** The pending destructive action awaiting confirmation. */
  const [pending, setPending] = useState<PendingAction | null>(null)
  /** Rows a reconnect has been started for, driving their busy state. */
  const [reconnectingIds, setReconnectingIds] = useState<Record<string, boolean>>({})

  /**
   * EVERY connection of the org. The status reconciliation, the Reconnect
   * survival and the rename target are judged on this list, never on the
   * facility-filtered one: a connection that has just been connected owns no
   * account at any facility yet, and must still be polled until it settles.
   */
  const rows = useMemo(() => connections ?? [], [connections])

  /** What the table shows: the whole list, or the facility's connections and counts. */
  const displayRows = useMemo(
    () =>
      scope
        ? scopeConnectionsToFacility(rows, facilityCounts.data ?? {}, {
            column: sortColumn,
            direction: sortDirection,
          })
        : rows,
    [scope, rows, facilityCounts.data, sortColumn, sortDirection],
  )
  const listLoading = isLoading || (!!scope && facilityCounts.isLoading)
  // Only a load that never produced data: a failed background refetch keeps
  // the cached list (and the facility's counts) on screen.
  const listError =
    (connections ? null : error) ?? (scope && !facilityCounts.data ? facilityCounts.error : null)

  // DCP-401: refresh the data (and toast, for syncs started here) when a
  // connection's sync finishes. Fed the query's own data, not `rows`, so the
  // loading state reaches it as `undefined` rather than as an empty list.
  //
  // It also owns the stall clock: a run that dies without a terminal status
  // goes idle by AGE alone, with no data change to re-render on, and the
  // watcher's tick (its state lives in THIS component) re-renders the section
  // at that instant. The memoised `columns` need no extra dep: they hold only
  // render FUNCTIONS, which DataTable (not memoised) calls on every render, and
  // each reads the clock afresh through getConnectionSyncActivity.
  useLedgerSyncCompletionWatcher(connections, dataUpdatedAt)

  /**
   * Connections that can be synced (AC#1/AC#6). `active` — rendered as
   * "Connected" — is the only status with a live link behind it:
   * `initiated`/`requires_action` have not finished connecting and
   * `failed`/`disconnected` need re-authorising first.
   *
   * BOTH "Sync all" buttons fan out over exactly this list (AC#25). Balances
   * used to fan out over active FACILITIES instead, which reached a connection
   * only through its accounts and therefore missed any Connected connection
   * whose accounts are all unassigned or all on deactivated facilities.
   */
  const activeConnections = useMemo(() => rows.filter((row) => row.status === 'active'), [rows])

  /**
   * The connection a Reconnect redirect left in flight, read once on mount.
   * `startReconnect` ends in a full-page redirect, so `reconnectingIds` is gone
   * when the user comes back; the redirect-survival state is not. Without it an
   * MX widget that dismisses instead of redirecting to the callback page would
   * leave a repaired connection looking broken until the webhook or the morning
   * reconcile. The poll clears the state once it settles or runs out
   * (`useReconcileLedgerSyncConnection`); the callback page clears it before
   * navigating back here, so after that flow this is null.
   */
  const [returnedFromReconnectId] = useState(() => getLedgerSyncOAuthState().connectionId)

  /**
   * Whether a row is being reconnected: clicked in this mount, or carried over
   * the redirect. The carried id counts only for a connection that owns
   * accounts — the same state is written for a FIRST connect, which is already
   * polled by its status and must keep stopping on `failed`.
   */
  const isReconnecting = useCallback(
    (row: BankConnectionRow) =>
      !!reconnectingIds[row.id] || (row.id === returnedFromReconnectId && row.accounts_count > 0),
    [reconnectingIds, returnedFromReconnectId],
  )

  /**
   * Connections that still need their status reconciled — the rows that get an
   * invisible {@link LedgerSyncConnectionReconciler} below.
   *
   * WHY THIS EXISTS AT ALL: the hosted widget is not guaranteed to redirect
   * back to `/ledgersync-callback`. MX-sourced connections just dismiss the
   * widget, and webhook delivery for `connection.active` is unreliable, so
   * without a client-side poll a connection sits at `initiated` /
   * `requires_action` forever and its accounts are never pulled. The callback
   * page covers only the flow that DOES navigate back; this is the other half.
   *
   * The row set is a connect still in progress — `initiated`, or
   * `requires_action` with no account yet ({@link isFirstConnectInProgress}) —
   * PLUS any row the user just clicked Reconnect on (DCP-173, AC.6).
   *
   * A `requires_action` connection that owns accounts is a broken connection,
   * a long-lived state: polling it would spend up to 15 vendor calls per page
   * open for nothing the user is doing. Its recovery is picked up by the
   * morning org reconcile or the webhook, or by this poll once the user clicks
   * Reconnect. Likewise a failed/disconnected row is only polled after
   * Reconnect, since a completed re-link has to flip it back to `active`.
   * Reconnect intent comes from {@link isReconnecting}.
   */
  const reconciling = useMemo(
    () =>
      rows.filter(
        (row) =>
          row.status === 'initiated' || isFirstConnectInProgress(row) || isReconnecting(row),
      ),
    [rows, isReconnecting],
  )

  /**
   * The row the rename dialog is editing, resolved from the CURRENT list rather
   * than captured on click, so a background refetch that resolves
   * "Connecting…" into the real bank name is picked up instead of showing a
   * stale snapshot.
   */
  const renameTarget = useMemo(
    () => rows.find((row) => row.id === renameId) ?? null,
    [rows, renameId],
  )

  /**
   * Re-consent at the EXISTING connection and redirect the browser to the
   * hosted page LedgerSync mints for it (DCP-324).
   *
   * THIS NO LONGER RUNS THE CONNECT FLOW. It used to call
   * `useCreateLedgerSyncWidget`, which starts a BRAND NEW connection — the
   * product owner reproduced the result: reconnecting the same bank produced a
   * second connection with new `con_`/`acc_`/`txn_` ids, duplicating the
   * accounts and defeating the transaction dedupe. `useReauthorizeConnection`
   * repairs the connection in place, so its ids survive, and it persists the
   * redirect-survival state (with this page as the return path) before we
   * navigate.
   *
   * @param connection - The connection row to re-authorise
   */
  const startReconnect = useCallback(
    (connection: BankConnectionRow) => {
      setReconnectingIds((prev) => ({ ...prev, [connection.id]: true }))
      reauthorize.mutate(
        { connectionId: connection.id, returnPath: basePath },
        {
          onSuccess: ({ reauthUrl, sourceChanged }) => {
            if (sourceChanged) {
              // The URL leads to a DIFFERENT provider, so opening it would
              // create a new connection rather than repair this one. The hook
              // has already told the user to use "Connect Bank" instead; here we
              // just release the row, because nothing is in flight for it.
              setReconnectingIds((prev) => {
                const next = { ...prev }
                delete next[connection.id]
                return next
              })
              return
            }
            // Same-tab redirect, matching the Connect Bank flow.
            window.location.href = reauthUrl
          },
          onError: () => {
            // Release the busy flag; the hook already surfaced the error.
            setReconnectingIds((prev) => {
              const next = { ...prev }
              delete next[connection.id]
              return next
            })
          },
        },
      )
    },
    [reauthorize, basePath],
  )

  /** Fire the confirmed destructive action, then close the dialog. */
  const confirmPending = useCallback(() => {
    if (!pending) return
    deleteConnection.mutate(pending.connection.id, { onSettled: () => setPending(null) })
  }, [pending, deleteConnection])

  const columns: DataTableColumn<BankConnectionRow>[] = useMemo(
    () => [
      {
        key: 'display_name',
        header: 'Institution',
        sortable: true,
        // Anchor column — never hideable: it is both the row's identity and the
        // only way into the connection's detail screen.
        disableHide: true,
        cellClassName: 'font-medium',
        // The whole row navigates into the connection's detail screen (see
        // `onRowClick` on the DataTable below), but the Institution cell keeps its own
        // <button> so the drill-down stays reachable by keyboard — rows
        // themselves are not focusable. `stopPropagation` here is required:
        // without it a click on the name would fire BOTH this button's
        // `onClick` and the row's `onRowClick` (same event bubbles from the
        // button up through the row), navigating twice.
        render: (connection) => {
          const name = connection.display_name ?? CONNECTION_COPY.unknownInstitution
          const showError =
            presentConnectionStatus(connection).unhealthy && !!connection.last_error
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                navigate(`${basePath}/connections/${connection.id}`)
              }}
              className="flex w-full items-center gap-3 text-left hover:underline"
            >
              {/* Decorative: the bank's name sits right beside it, so the
                  avatar adds nothing for a screen reader. Without aria-hidden
                  the initials join this button's accessible name and it reads
                  "C H Chase" instead of "Chase". */}
              <Avatar aria-hidden="true" className="h-8 w-8 shrink-0">
                {connection.logo_url && !brokenLogos[connection.id] && (
                  <AvatarImage
                    src={connection.logo_url}
                    alt=""
                    onError={() =>
                      setBrokenLogos((prev) => ({ ...prev, [connection.id]: true }))
                    }
                  />
                )}
                {/* Initials, like the prototype, rather than one generic
                    landmark glyph on every row: with a logo missing the icon
                    made every bank look identical, which is the opposite of
                    what an avatar column is for. Falls back to the glyph only
                    when the name yields no letters at all. */}
                <AvatarFallback className="text-xs font-semibold">
                  {bankInitials(name) || <Landmark className="h-4 w-4 text-muted-foreground" />}
                </AvatarFallback>
              </Avatar>
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{name}</span>
                {showError && (
                  <span className="truncate text-xs text-destructive">
                    {connection.last_error}
                  </span>
                )}
              </span>
            </button>
          )
        },
      },
      {
        key: 'capabilities',
        header: 'Capabilities',
        sortable: true,
        render: (connection) => (
          <CapabilityBadges capabilities={connection.realized_capabilities} />
        ),
      },
      {
        key: 'accounts_count',
        header: 'Accounts & cards',
        sortable: true,
        // Left, not right: the cell reads "7 accounts", and a right-aligned
        // phrase drifts away from its header. Right alignment is for bare
        // figures you scan down a column — this one is a sentence.
        render: (connection) => (
          <span className="text-sm">
            {connection.accounts_count}{' '}
            {connection.accounts_count === 1 ? 'account' : 'accounts'}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        render: (connection) => <ConnectionStatusBadge connection={connection} />,
      },
      {
        key: 'last_sync_at',
        header: 'Sync date',
        sortable: true,
        render: (connection) => {
          // DCP-401: the SERVER's pipeline state drives this, for as long as the
          // run takes; `isPending` only covers the sub-second click → response
          // gap before the refetched row carries `queued`.
          const activity = getConnectionSyncActivity(connection)
          const clicked = syncConnection.isPending && syncConnection.variables === connection.id
          // "Never synced" rather than a dash: the distinction between "no sync
          // has ever run" and "the last one produced nothing" is the first thing
          // asked about a bank that shows no transactions.
          const syncLine =
            activity.active || clicked ? (
              <span
                role="status"
                className="flex items-center gap-1.5 text-sm text-muted-foreground"
              >
                <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
                {activity.label ?? 'Syncing…'}
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                {connection.last_sync_at ? formatDateTime(connection.last_sync_at) : 'Never synced'}
                <SyncFailureIndicator failures={activity.failures} />
              </span>
            )
          // The vendor's date says how old the bank's figures are, independent
          // of when we last fetched them; it matters exactly when the two
          // diverge, e.g. on a connection that went unhealthy.
          const dataAsOf = bankDataAsOfLabel(connection.data_refreshed_at)
          return (
            <span className="flex flex-col gap-0.5">
              {syncLine}
              {dataAsOf && <span className="text-xs text-muted-foreground">{dataAsOf}</span>}
            </span>
          )
        },
      },
      {
        key: 'actions',
        header: '',
        headerLabel: 'Actions',
        align: 'right',
        disableHide: true,
        render: (connection) => {
          const canSync = connection.status === 'active'
          // Busy while EITHER pipeline runs for this connection (DCP-401): the
          // per-connection Sync moves both, so a second press mid-run could
          // only re-enqueue work already in flight.
          const activity = getConnectionSyncActivity(connection)
          const busySync =
            activity.active ||
            (syncConnection.isPending && syncConnection.variables === connection.id)
          // Reconnect needs one thing: a CANONICAL connection id for the vendor
          // to reauthorize. It is deliberately NOT gated on status — the
          // endpoint is not, and running it on an ACTIVE connection re-opens the
          // bank's account-selection step, which on FINICITY is the only
          // supported way to pick up an account the user opened after linking.
          // (It used to be gated on `institution_id` instead, because Reconnect
          // re-ran the connect flow; that whole read is gone with the bug.)
          const canReconnect = !!connection.ledgersync_connection_id
          return (
            // Guard against the row's `onRowClick` (see DataTable below). The
            // dropdown's menu content renders through a Radix Portal — physically
            // outside this cell's DOM subtree — but React bubbles synthetic
            // events along the COMPONENT tree, not the DOM tree, so a click on
            // a portaled menu item still bubbles up through this wrapper before
            // it would ever reach the row. Stopping it here therefore covers
            // both the trigger click (opens the menu) and every item click
            // (fires the action) without either one navigating the row.
            // `onKeyDown` covers Enter/Space activation from the keyboard.
            <div
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <ReadOnlyActionMenu
                readOnly={readOnly}
                ariaLabel={`Actions for ${connection.display_name ?? 'connection'}`}
                items={[
                  {
                    id: 'rename',
                    label: CONNECTION_COPY.renameTitle,
                    onClick: () => setRenameId(connection.id),
                  },
                  // AC#6: Sync is offered ONLY for an active connection. A
                  // non-active row has no live link, and the edge function
                  // answers 409 for it — so the item is omitted rather than
                  // shown disabled, which would invite the click.
                  ...(canSync
                    ? [
                        {
                          id: 'sync',
                          label: busySync ? (activity.label ?? 'Syncing…') : 'Sync',
                          disabled: busySync,
                          onClick: () => syncConnection.mutate(connection.id),
                        },
                      ]
                    : []),
                  {
                    id: 'reconnect',
                    label: 'Reconnect',
                    // A connection the aggregator never named has nothing to
                    // repair; disabled (not hidden) so the absence is visible.
                    disabled: !canReconnect || !!reconnectingIds[connection.id],
                    onClick: () => startReconnect(connection),
                  },
                  {
                    id: 'delete',
                    label: CONNECTION_COPY.deleteConnectionCta,
                    variant: 'danger' as const,
                    onClick: () => setPending({ kind: 'delete', connection }),
                  },
                ]}
              />
            </div>
          )
        },
      },
    ],
    [
      basePath,
      brokenLogos,
      navigate,
      readOnly,
      reconnectingIds,
      startReconnect,
      syncConnection,
    ],
  )

  // Per-user column visibility + order, keyed 'bank-connections'.
  const columnPrefs = useTableColumnPrefs('bank-connections', columns)

  /**
   * AC#1: the two "Sync all" buttons exist only when at least one connection is
   * `active`. With none, both would be guaranteed no-ops — the fan-out has
   * nothing to target — and a disabled pair of buttons reads as a broken
   * feature rather than as an empty org.
   */
  // Sync all is org-level: never on a facility's page.
  const showSyncAll = !scope && activeConnections.length > 0

  /**
   * "Sync all" over the connected banks whose pipeline is NOT already running
   * (DCP-401). Re-enqueueing a live row starts a second run of the same
   * pipeline beside the first — the edge's idempotency key only dedupes within
   * one minute — which doubles the reads and makes the row's status follow
   * whichever run started last. Judged at click time, so the clock is fresh.
   *
   * @param pipeline - Which "Sync all" button was pressed
   */
  const startSyncAll = (pipeline: LedgerSyncSyncPipeline) => {
    const targets = activeConnections
      .filter((row) => !isPipelineSyncActive(row, pipeline))
      .map((row) => row.id)
    if (targets.length === 0) {
      // Names the pipeline (`'transactions'` / `'balances'` read as the words
      // themselves): the other one may well be idle on the same banks, and a
      // bare "already syncing" after "Sync all balances" reads as if the
      // balances were running when only the transactions are.
      toast.info(`Every connection is already syncing ${pipeline}`)
      return
    }
    if (pipeline === 'transactions') syncAll.mutate(targets)
    else syncBalancesAll.mutate(targets)
  }

  return (
    <>
      {/* One reconciler per pending row: a separate element per connection keeps
          each poll's attempt counter (and its fast→slow backoff) independent,
          and keeps the hook call count stable — hooks cannot be called in a
          loop. Each renders null, so nothing is added to the layout. No
          facilityId is passed: a connection is org-level since DCP-324, so the
          poll refreshes the org-level caches this section reads. */}
      {reconciling.map((row) => (
        <LedgerSyncConnectionReconciler
          key={row.id}
          connectionId={row.id}
          // A row polled ONLY because of a Reconnect has a terminal stored
          // status; tell the poll so failed/disconnected do not stop it after a
          // single request (DCP-173, AC.6).
          reconnecting={isReconnecting(row)}
        />
      ))}
      <Card>
        <CardContent className="space-y-4 p-6">
          {/* Header block, composed by hand rather than via PageHeader: its
              `title` prop is typed `string`, so the "LedgerSync" chip beside the
              title could only get in there as part of the text. */}
          <div className="space-y-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 shrink-0" />
                <h2 className="text-xl font-semibold tracking-tight">
                  {CONNECTION_COPY.sectionTitle}
                </h2>
                <Badge variant="secondary">{CONNECTION_COPY.sourceChip}</Badge>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {CONNECTION_COPY.sectionSubtitle}
              </p>
            </div>
            {/* Action bar on its OWN row, which is what lets the two groups sit on
                opposite edges: the org-wide syncs ("refresh what is already here")
                on the left, the connect CTA ("add something new") on the right.
                Sharing the title's row would pin the syncs to the middle, since
                the title owns the left edge.

                The left group is rendered even when `showSyncAll` is false — an
                empty flex child still holds the left edge, so `justify-between`
                keeps the connect button on the right instead of letting it slide
                across to where the syncs would have been. */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {showSyncAll && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={syncAll.isPending}
                      onClick={() => startSyncAll('transactions')}
                    >
                      {syncAll.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-2 h-4 w-4" />
                      )}
                      Sync all transactions
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={syncBalancesAll.isPending}
                      onClick={() => startSyncAll('balances')}
                    >
                      {syncBalancesAll.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-2 h-4 w-4" />
                      )}
                      Sync all balances
                    </Button>
                  </>
                )}
              </div>
              {/* The connect flow has no facility (a connection is org-level);
                  the scope only sets where the user returns and what the
                  manual fallback pre-selects. */}
              <LedgerSyncConnectButton scope={scope} />
            </div>
          </div>

          {listLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : listError ? (
            <div
              role="alert"
              className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
            >
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {listError instanceof Error ? listError.message : 'Failed to load connections'}
            </div>
          ) : (
            <DataTable<BankConnectionRow>
              columns={columns}
              data={displayRows}
              rowKey={(connection) => connection.id}
              // First `onRowClick` usage in DCP. The library's row `onClick`
              // (data-table/component.tsx) is `() => onRowClick(row, rowIndex)` —
              // it does NOT forward the DOM event — so propagation cannot be
              // stopped from here. Any interactive cell that must NOT trigger
              // navigation (the kebab below) has to stop propagation itself, on
              // its own wrapper, before the click bubbles up to this row.
              // `cursor-pointer`/hover feedback come from the library automatically
              // whenever `onRowClick` is set — no `rowClassName` needed for that.
              onRowClick={(connection) => navigate(`${basePath}/connections/${connection.id}`)}
              sorting={{ mode: 'server', sortState, onSortChange: setSortState }}
              emptyState={
                <EmptyState
                  icon={Building2}
                  title={scope ? CONNECTION_COPY.facilityEmptyTitle : CONNECTION_COPY.emptyTitle}
                  description={
                    scope
                      ? facilityConnectionsEmptyDescription(scope.facilityName)
                      : CONNECTION_COPY.emptyDescription
                  }
                />
              }
              itemName="connections"
              // The section header above is the table's title, so the Columns
              // menu is opted into explicitly rather than via `title`.
              showColumnControls
              enableColumnReorder
              {...columnPrefs}
            />
          )}
        </CardContent>

        <RenameConnectionDialog
          open={renameId !== null}
          onOpenChange={(open) => {
            if (!open) setRenameId(null)
          }}
          connection={renameTarget}
        />

        {/*
          AC#7. `open` keys off the intent, not off a derived row, so the dialog
          stays open through the mutation even as the row leaves the list on
          success. Close requests are ignored while the delete is in flight.
        */}
        <ConfirmationDialog
          open={pending !== null}
          onOpenChange={(open) => {
            if (!open && !deleteConnection.isPending) setPending(null)
          }}
          title={
            pending
              ? `Delete ${pending.connection.display_name ?? 'this connection'}?`
              : 'Delete connection?'
          }
          description={CONNECTION_COPY.deleteConnectionDescription}
          confirmText={CONNECTION_COPY.deleteConnectionCta}
          cancelText="Cancel"
          variant="destructive"
          isLoading={deleteConnection.isPending}
          onConfirm={confirmPending}
        />
      </Card>
    </>
  )
}
